import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { ClienteDaApi, PedidoDeLogin } from "@/api/cliente";
import type { Papel } from "@/api/contrato";
import { LojaDaSessao, type EstadoDaSessao } from "./loja";
import { alcanca, papelMaximo } from "./papeis";

export interface Sessao {
  estado: EstadoDaSessao;
  cliente: ClienteDaApi;
  entrar: (pedido: PedidoDeLogin) => Promise<void>;
  sair: () => Promise<void>;
  /** O operador alcança este papel? Decide o que a TELA mostra, nada mais. */
  pode: (minimo: Papel) => boolean;
  papel: Papel | null;
}

export const ContextoDaSessao = createContext<Sessao | null>(null);

export function ProvedorDaSessao({
  children,
  loja: fornecida,
}: {
  children: ReactNode;
  /** Injetável para teste. Em produção a loja é criada aqui, uma vez. */
  loja?: LojaDaSessao;
}) {
  // Inicializador preguiçoso: sem ele, uma loja nova (com um cliente novo) seria
  // construída a cada renderização e descartada em seguida.
  const [loja] = useState(() => fornecida ?? new LojaDaSessao());

  const estado = useSyncExternalStore(loja.inscrever, loja.ler, loja.ler);

  useEffect(() => {
    void loja.retomar();
    return () => loja.descartar();
  }, [loja]);

  const valor = useMemo<Sessao>(() => {
    const papeis = estado.fase === "dentro" ? estado.papeis : [];
    return {
      estado,
      cliente: loja.cliente,
      entrar: (pedido) => loja.entrar(pedido),
      sair: () => loja.sair(),
      pode: (minimo: Papel) => alcanca(papeis, minimo),
      papel: papelMaximo(papeis),
    };
  }, [estado, loja]);

  return <ContextoDaSessao value={valor}>{children}</ContextoDaSessao>;
}
